// src/services/mockSalesService.js
// Drop-in mock replacement for salesService.
// Mirrors every method the app calls — no real API requests.

import {
  MOCK_SALES_RAW_DATA,
  MOCK_SALES_KPI,
  MOCK_SALES_TEAM_DATA,
  MOCK_SALES_CALL_STATUS,
  MOCK_SALES_OFFERS,
  MOCK_SALES_HEATMAP,
  MOCK_SALES_FILTER_OPTIONS,
  MOCK_LAST_UPDATED,
} from './mockData';

// ── Helpers ───────────────────────────────────────────────────────────────────

const delay = (ms = 300) => new Promise((r) => setTimeout(r, ms));

const filterRawData = (rawData, filters = {}) => {
  let out = [...rawData];
  const { closer, offer_type, start_date, end_date } = filters;

  if (closer && closer !== 'all') {
    out = out.filter((r) => r.opportunity_closer === closer);
  }
  if (offer_type && offer_type !== 'all') {
    out = out.filter((r) => r.offer_type === offer_type);
  }
  if (start_date) {
    out = out.filter((r) => {
      const d = r.appointment_date || r.appointment_completed_date;
      return d && d >= start_date;
    });
  }
  if (end_date) {
    out = out.filter((r) => {
      const d = r.appointment_date || r.appointment_completed_date;
      return d && d <= end_date;
    });
  }
  return out;
};

// ── KPI calculation (mirrors salesService.calculateKPIs) ─────────────────────

const calculateKPIs = (rawData, filters = {}) => {
  const data = filterRawData(rawData, filters);
  if (!data.length) return _getEmptyKPIs();

  const showed   = data.filter((r) => r.status_category === 'showed').length;
  const won      = data.filter((r) => r.deal_is_won).length;
  const offered  = data.filter((r) => r.offers_sent).length;
  const value    = data.filter((r) => r.deal_is_won).reduce((s, r) => s + (r.won_revenue || 0), 0);

  return {
    total_calls:          data.length,
    show_rate:            data.length > 0 ? Math.round((showed / data.length) * 100) : 0,
    deals_offered:        offered,
    appointments_offered: offered,
    deals_closed:         won,
    opportunity_value:    value,
    conversion_rate:      offered > 0 ? Math.round((won / offered) * 100) : 0,
  };
};

// ── Team performance (mirrors salesService.calculateTeamPerformance) ──────────

const calculateTeamPerformance = (rawData, closer = 'all') => {
  const data = closer === 'all' ? rawData : rawData.filter((r) => r.opportunity_closer === closer);

  const byCloser = {};
  data.forEach((r) => {
    const name = r.opportunity_closer || 'Unknown';
    if (!byCloser[name]) {
      byCloser[name] = { name, calls: 0, appointments: 0, showed: 0, no_show: 0, deals: 0, deal_value: 0 };
    }
    byCloser[name].calls++;
    byCloser[name].appointments++;
    if (r.status_category === 'showed') byCloser[name].showed++;
    if (r.status_category === 'no_show') byCloser[name].no_show++;
    if (r.deal_is_won) { byCloser[name].deals++; byCloser[name].deal_value += r.won_revenue || 0; }
  });

  return Object.values(byCloser);
};

// ── Call status (mirrors salesService.calculateCallStatus) ───────────────────

const calculateCallStatus = (rawData, closer = 'all') => {
  const data = closer === 'all' ? rawData : rawData.filter((r) => r.opportunity_closer === closer);
  return {
    total:     data.length,
    showed:    data.filter((r) => r.status_category === 'showed').length,
    no_show:   data.filter((r) => r.status_category === 'no_show').length,
    canceled:  data.filter((r) => r.status_category === 'canceled').length,
    confirmed: data.filter((r) => r.status_category === 'confirmed').length,
    closer,
  };
};

// ── Offers (mirrors salesService.calculateOffers) ─────────────────────────────

const calculateOffers = (rawData, closer = 'all', offerType = 'all') => {
  let data = rawData.filter((r) => r.offers_sent);
  if (closer !== 'all')    data = data.filter((r) => r.opportunity_closer === closer);
  if (offerType !== 'all') data = data.filter((r) => r.offer_type === offerType);

  const total = data.length;
  const byType = {};
  data.forEach((r) => { byType[r.offer_type] = (byType[r.offer_type] || 0) + 1; });

  return {
    total_sent:         total,
    high_probability:   Math.round(total * 0.4),
    medium_probability: Math.round(total * 0.35),
    low_probability:    Math.round(total * 0.25),
    by_type:            byType,
  };
};

// ── Empty KPIs ────────────────────────────────────────────────────────────────

const _getEmptyKPIs = () => ({
  total_calls: 0, show_rate: 0, deals_offered: 0,
  appointments_offered: 0, deals_closed: 0,
  opportunity_value: 0, conversion_rate: 0,
});

// ── Main service object ───────────────────────────────────────────────────────

const mockSalesService = {

  // Called by useSalesData
  async getBulkSalesData(params = {}) {
    await delay(400);
    const raw = filterRawData(MOCK_SALES_RAW_DATA, params);
    return {
      raw_data: raw,
      filter_metadata: {
        available_closers:       MOCK_SALES_FILTER_OPTIONS.closers.filter((c) => c !== 'all'),
        available_offer_types:   MOCK_SALES_FILTER_OPTIONS.offer_types.filter((t) => t !== 'all'),
        available_statuses:      MOCK_SALES_FILTER_OPTIONS.statuses.filter((s) => s !== 'all'),
        available_pipeline_stages: MOCK_SALES_FILTER_OPTIONS.pipeline_stages.filter((s) => s !== 'all'),
      },
    };
  },

  // Called by useSalesData
  async getDataFreshness() {
    await delay(100);
    return { data_last_updated: MOCK_LAST_UPDATED };
  },

  // Called by useSalesData & KPISection
  calculateKPIs,
  calculateTeamPerformance,
  calculateCallStatus,
  calculateOffers,
  _getEmptyKPIs,

  // Called by useSalesHeatmap
  // Returns rows = time periods, headers = category labels (call types / closers / offer types)
  // Each cell has value + percentage (share of that category in that time period)
  async getHeatmapData(tabType, params = {}) {
    await delay(350);
    const tab = MOCK_SALES_HEATMAP[tabType];
    if (!tab) return { headers: [], rows: [], totals: [], empty: true, tab_type: tabType };
    const dimension = params.dimension || Object.keys(tab)[0];
    const raw = tab[dimension];
    if (!raw) return { headers: [], rows: [], totals: [], empty: true, tab_type: tabType };

    // raw has: headers = week labels, rows = categories with cells per week
    // Component expects: headers = category labels, rows = weeks with cells per category
    // We need to TRANSPOSE: rows become time periods, columns become categories

    const categoryLabels = raw.rows.map(r => r.label);    // e.g. ['Showed','No Show',...]
    const weekLabels     = raw.headers;                    // e.g. ['W8 (Mar 20)', ...]

    // Build week rows — each row is one week, cells are per category
    const transposedRows = weekLabels.map((week, wi) => {
      const cells = raw.rows.map(catRow => ({
        value: catRow.cells[wi]?.value || 0,
        percentage: 0, // calculated below
      }));
      const rowTotal = cells.reduce((s, c) => s + c.value, 0);
      // Percentage = share of this category within this week
      cells.forEach(c => {
        c.percentage = rowTotal > 0 ? +((c.value / rowTotal) * 100).toFixed(1) : 0;
      });
      return { week, label: week, cells, total: rowTotal };
    });

    // Column totals (total per category across all weeks)
    const colTotals = categoryLabels.map((_, ci) =>
      transposedRows.reduce((s, row) => s + (row.cells[ci]?.value || 0), 0)
    );

    // Scaling info — column-wise so each category scales independently
    const ranges = categoryLabels.map((_, ci) => {
      const vals = transposedRows.map(r => r.cells[ci]?.percentage || 0);
      return { min: Math.min(...vals), max: Math.max(...vals) };
    });

    return {
      headers:      categoryLabels,
      rows:         transposedRows,
      totals:       colTotals,
      dimension,
      tab_type:     tabType,
      empty:        false,
      scaling_info: { type: 'column', ranges },
      metadata:     { total_records: transposedRows.reduce((s, r) => s + r.total, 0) },
    };
  },

  async getHeatmapDimensions(tabType) {
    await delay(100);
    const dimensionMap = {
      sales_calls:  [
        { id: 'call_type', label: 'Call Status',   description: 'Group by appointment status' },
        { id: 'closer',    label: 'Team Member',   description: 'Group by closer' },
      ],
      offers_sent:  [
        { id: 'offer_type',   label: 'Offer Type',   description: 'Group by offer package' },
        { id: 'probability',  label: 'Probability',  description: 'Group by closing probability' },
      ],
      closed_deals: [
        { id: 'person',     label: 'Team Member', description: 'Group by closer' },
        { id: 'offer_type', label: 'Offer Type',  description: 'Group by offer package' },
      ],
    };
    return { dimensions: dimensionMap[tabType] || [] };
  },

  // Called by useFilterOptions
  async getAvailableClosers() {
    await delay(100);
    return { closers: MOCK_SALES_FILTER_OPTIONS.closers.filter((c) => c !== 'all') };
  },

  async getAvailableOfferTypes() {
    await delay(100);
    return {
      offer_types: MOCK_SALES_FILTER_OPTIONS.offer_types
        .filter((t) => t !== 'all')
        .map((id) => ({ id: id.toLowerCase(), label: id })),
    };
  },

  // Cache stubs (useSalesData calls these)
  clearCache()  { /* no-op for mock */ },
  getCacheInfo() { return { isValid: true, cacheAge: 0 }; },
  isCacheValid() { return true; },
};

export default mockSalesService;